import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ScrollText, CheckCircle2, Play, Timer as TimerIcon } from "lucide-react";
import { SubjectPicker, GradePicker } from "@/components/CurriculumPicker";
import GenerateLoader from "@/components/ui/GenerateLoader";
import StoryQuizPlayer from "@/components/quiz/StoryQuizPlayer";
import AttachmentPicker from "@/components/quiz/AttachmentPicker";
import MobiHint from "@/components/MobiHint";

const TYPES = ["mcq", "true_false", "short_answer", "fill_blank"];
const TIMER_OPTIONS = [
  { value: 0,   label: "No timer" },
  { value: 30,  label: "30 s" },
  { value: 45,  label: "45 s" },
  { value: 60,  label: "60 s" },
  { value: 90,  label: "90 s" },
  { value: 120, label: "2 min" },
];

export default function AIQuizMaker({ studentView }) {
  const [params] = useSearchParams();
  const initSubjectId = params.get("subjectId") || "";
  const [form, setForm] = useState({
    subject_id: initSubjectId,
    subject: params.get("subject") || "",
    grade_id: "",
    grade: params.get("grade") || "",
    topic: "",
    num_questions: 5,
    difficulty: "medium",
    question_types: ["mcq"],
  });
  const [loading, setLoading] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const [instructions, setInstructions] = useState("");
  const [attachmentText, setAttachmentText] = useState("");
  const [history, setHistory] = useState([]);
  const [timePerQ, setTimePerQ] = useState(60);
  const [playing, setPlaying] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleType = (t) => setForm((f) => ({ ...f, question_types: f.question_types.includes(t) ? f.question_types.filter((x) => x !== t) : [...f.question_types, t] }));

  const load = async () => {
    try {
      const url = form.subject_id ? `/ai/quiz?subject_id=${form.subject_id}` : "/ai/quiz";
      const { data } = await api.get(url);
      setHistory(data.quizzes || []);
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [form.subject_id]);

  const generate = async (e) => {
    e?.preventDefault();
    if (!form.subject_id) return toast.error("Pick a subject");
    if (!form.topic) return toast.error("Topic is required");
    setLoading(true); setQuiz(null);
    try {
      const { data } = await api.post("/ai/quiz/generate", {
        subject: form.subject,
        topic: form.topic,
        grade: form.grade,
        num_questions: Number(form.num_questions) || 5,
        difficulty: form.difficulty,
        question_types: form.question_types,
        subject_id: form.subject_id,
        instructions,
        attachment_text: attachmentText,
        save: true,
      });
      setQuiz(data.quiz);
      toast.success("Quiz generated & saved to your dashboard");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="quiz-maker-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">/ ai quiz maker{form.subject_id ? ` · ${form.subject}` : ""}</div>
        <h1 className="font-display font-bold text-3xl tracking-tighter">{studentView ? "My quizzes" : "Generate a quiz with AI"}</h1>
        <p className="text-sm text-neutral-600 mt-1">5 credits per generation.{form.subject_id ? " · Scoped to this subject." : ""}</p>
      </div>

      {!studentView && (
        <form onSubmit={generate} className="bg-white border border-neutral-200 p-6 grid lg:grid-cols-2 gap-4">
          <SubjectPicker
            testid="quiz-subject"
            className="rounded-md"
            value={form.subject_id}
            onChange={(sid, sname, gid, gname) => setForm({ ...form, subject_id: sid, subject: sname, grade_id: gid || form.grade_id, grade: gname || form.grade })}
          />
          <input className={inp} placeholder="Topic *" required value={form.topic} onChange={set("topic")} data-testid="quiz-topic" />
          <GradePicker
            testid="quiz-grade"
            className="rounded-md"
            includeNone
            value={form.grade_id}
            onChange={(gid, gname) => setForm({ ...form, grade_id: gid, grade: gname })}
          />
          <select className={inp} value={form.difficulty} onChange={set("difficulty")}>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>
          <input className={inp} type="number" min={1} max={20} placeholder="Number of questions" value={form.num_questions} onChange={set("num_questions")} data-testid="quiz-num" />
          <div>
            <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500 flex items-center gap-1.5"><TimerIcon size={11} /> Time per question</div>
            <select
              value={timePerQ}
              onChange={(e) => setTimePerQ(Number(e.target.value))}
              data-testid="quiz-time-per-question"
              className={inp}
            >
              {TIMER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="lg:col-span-2 flex flex-wrap gap-2">
            {TYPES.map((t) => (
              <button type="button" key={t} onClick={() => toggleType(t)} className={`text-xs px-3 py-1.5 border ${form.question_types.includes(t) ? "bg-[#FFD600] text-[#0A0C10] border-transparent" : "border-neutral-300"}`}>{t}</button>
            ))}
          </div>
          <textarea
            className={`${inp} lg:col-span-2 resize-none`}
            rows={2}
            placeholder="Extra instructions for the AI (optional) — e.g. focus on key dates, avoid trick questions"
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            data-testid="quiz-instructions"
          />
          <div className="lg:col-span-2">
            <AttachmentPicker onChange={setAttachmentText} />
          </div>
          <button type="submit" disabled={loading} data-testid="quiz-generate-btn" className="lg:col-span-2 bg-[#0A0C10] text-white font-bold py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {loading
              ? <GenerateLoader label="Generating quiz" phrases={["Reading topic…", "Drafting questions…", "Balancing difficulty…", "Adding explanations…"]} />
              : <><Sparkles size={16} /> Generate quiz with AI</>}
          </button>
        </form>
      )}

      {quiz && (
        <div className="bg-white border border-neutral-200 p-6" data-testid="quiz-generated">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">/ generated</div>
              <h2 className="font-display font-bold text-2xl">{quiz.title}</h2>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">{quiz.questions.length} questions</span>
              <button
                onClick={() => setPlaying(true)}
                data-testid="quiz-play-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#FFD600] text-[#0A0C10] text-sm font-bold hover:scale-[1.02] transition-transform"
              >
                <Play size={14} /> Play in story mode
              </button>
            </div>
          </div>
          <ol className="space-y-4">
            {quiz.questions.map((q, i) => (
              <li key={i} className="border-l-2 border-[#FFD600] pl-4">
                <div className="font-medium text-sm">{i + 1}. {q.question}</div>
                {q.options?.length > 0 && (
                  <ul className="mt-2 ml-4 text-sm text-neutral-700 space-y-1">
                    {q.options.map((o, oi) => (
                      <li key={oi} className={o === q.correct_answer ? "text-green-700 font-semibold" : ""}>
                        {String.fromCharCode(65 + oi)}. {o} {o === q.correct_answer && <CheckCircle2 size={12} className="inline ml-1" />}
                      </li>
                    ))}
                  </ul>
                )}
                {!q.options?.length && (
                  <div className="mt-1 text-xs text-neutral-500">Answer: <span className="text-green-700">{q.correct_answer}</span></div>
                )}
                {q.explanation && <div className="text-xs text-neutral-500 mt-1">💡 {q.explanation}</div>}
              </li>
            ))}
          </ol>
        </div>
      )}

      <div>
        <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-2">/ recent quizzes</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
          {history.length === 0 ? (
            <div className="bg-white p-6 text-sm text-neutral-500 col-span-full text-center">No quizzes yet.</div>
          ) : history.map((q) => (
            <button
              key={q.id}
              onClick={() => { setQuiz(q); setPlaying(true); }}
              data-testid={`quiz-history-play-${q.id}`}
              className="bg-white p-5 text-left hover:bg-neutral-50 transition-colors group"
            >
              <div className="flex items-start justify-between">
                <ScrollText size={20} className="text-[#002FA7]" />
                <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-400 opacity-0 group-hover:opacity-100 inline-flex items-center gap-1">
                  <Play size={10} /> Play
                </span>
              </div>
              <div className="mt-3 font-display font-semibold">{q.title}</div>
              <div className="text-xs text-neutral-500 mt-1">{q.subject} · {q.topic} · {(q.questions || []).length} q's</div>
            </button>
          ))}
        </div>
      </div>

      <StoryQuizPlayer
        open={playing}
        onClose={() => setPlaying(false)}
        quiz={quiz}
        timePerQ={timePerQ}
      />
      <MobiHint
        label="Ask Mobi for quiz ideas"
        prompt={form.topic ? `Suggest 5 more quiz questions for topic: ${form.topic} (${form.subject || "a subject"}, ${form.difficulty} difficulty).` : "Give me a great quiz topic idea for my students."}
      />
    </div>
  );
}

const inp = "w-full bg-white border border-neutral-300 px-4 py-2.5 focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none";
